import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, Users, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResourceAllocations = () => {
  const { getAuthHeader } = useAuth();
  const [allocations, setAllocations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    resource_type: 'equipment',
    resource_id: '',
    allocated_from: '',
    allocated_to: '',
    quantity: 1
  });

  const fetchData = useCallback(async () => {
    try {
      const [allocRes, projRes, equipRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/resources/allocations`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/projects`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/resources/equipment`, { headers: getAuthHeader() })
      ]);
      setAllocations(allocRes.data);
      setProjects(projRes.data || []);
      setEquipment(equipRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/erp/resources/allocations`,
        formData,
        { headers: getAuthHeader() }
      );
      fetchData();
      setShowModal(false);
      setFormData({ project_id: '', resource_type: 'equipment', resource_id: '', allocated_from: '', allocated_to: '', quantity: 1 });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleRelease = async (id) => {
    if (!window.confirm('Release this allocation?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/erp/resources/allocations/${id}`, {
        headers: getAuthHeader()
      });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getProjectName = (id) => projects.find(p => p.id === id)?.name || projects.find(p => p.id === id)?.project_name || 'Unknown';
  const getEquipmentName = (id) => equipment.find(e => e.id === id)?.name || 'Unknown';

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Resource Allocations</h1>
          <p className="text-gray-600">Assign equipment and resources to projects</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-primary">
          <Plus className="w-4 h-4 mr-2" />
          Allocate Resource
        </Button>
      </div>

      <div className="grid gap-4">
        {allocations.map(alloc => (
          <Card key={alloc.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-bold text-lg">{getProjectName(alloc.project_id)}</h3>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                    {alloc.resource_type}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Resource:</span>
                    <p className="font-medium">
                      {alloc.resource_type === 'equipment' ? getEquipmentName(alloc.resource_id) : alloc.resource_id}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">From:</span>
                    <p className="font-medium">{new Date(alloc.allocated_from).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">To:</span>
                    <p className="font-medium">{new Date(alloc.allocated_to).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Quantity:</span>
                    <p className="font-medium">{alloc.quantity}</p>
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRelease(alloc.id)}>
                Release
              </Button>
            </div>
          </Card>
        ))}
        {allocations.length === 0 && (
          <Card className="p-12 text-center">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No resource allocations</p>
          </Card>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Allocate Resource</h2>
              <Button size="sm" variant="ghost" onClick={() => setShowModal(false)}>
                <X size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Project</label>
                <select
                  value={formData.project_id}
                  onChange={(e) => setFormData({...formData, project_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name || p.project_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Resource Type</label>
                <select
                  value={formData.resource_type}
                  onChange={(e) => setFormData({...formData, resource_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="equipment">Equipment</option>
                  <option value="labor">Labor</option>
                </select>
              </div>
              {formData.resource_type === 'equipment' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Equipment</label>
                  <select
                    value={formData.resource_id}
                    onChange={(e) => setFormData({...formData, resource_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Select Equipment</option>
                    {equipment.filter(e => e.status === 'available').map(e => (
                      <option key={e.id} value={e.id}>{e.name} ({e.equipment_id})</option>
                    ))}
                  </select>
                </div>
              )}
              {formData.resource_type === 'labor' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Labor ID/Name</label>
                  <input
                    type="text"
                    value={formData.resource_id}
                    onChange={(e) => setFormData({...formData, resource_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Enter labor ID or name"
                    required
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">From Date</label>
                  <input
                    type="date"
                    value={formData.allocated_from}
                    onChange={(e) => setFormData({...formData, allocated_from: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">To Date</label>
                  <input
                    type="date"
                    value={formData.allocated_to}
                    onChange={(e) => setFormData({...formData, allocated_to: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Quantity</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="1"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">Allocate</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceAllocations;